import { COUNTRIES } from '@/lib/constants/demographics'

export interface AudienceFilterCriteria {
  categoryTokens: Array<string>
  countryCodes: Array<string>
  audienceType: 'B2B' | 'B2C' | null
}

/**
 * Filters an array of audiences by category, country, and audience type.
 * Works with both AudienceSummary and Population shapes — the relevant fields
 * (industryLabels, contentLabels, audienceLabels) exist at runtime on both.
 *
 * Returns null when no filters are active (caller should fall through to
 * unfiltered display).
 */
export function filterAudiences<T extends { audienceType?: string | null }>(
  audiences: Array<T>,
  criteria: AudienceFilterCriteria,
): Array<T> | null {
  const hasActive = !!(
    criteria.categoryTokens.length > 0 ||
    criteria.countryCodes.length > 0 ||
    criteria.audienceType
  )
  if (!hasActive) return null

  const categorySet = new Set(criteria.categoryTokens)

  return audiences.filter((a) => {
    if (criteria.audienceType && a.audienceType !== criteria.audienceType) return false

    if (criteria.categoryTokens.length > 0) {
      const rec = a as Record<string, unknown>
      const industryTokens = (rec.industryLabels as Array<{ token: string }> | undefined)
        ?.map((l) => l.token) ?? []
      const contentLabels = rec.contentLabels as Array<{ token: string }> | undefined
      const categoryMatch = industryTokens.length > 0
        ? industryTokens.some((t) => categorySet.has(t))
        : contentLabels?.some((l) => categorySet.has(l.token))
      if (!categoryMatch) return false
    }

    if (criteria.countryCodes.length > 0) {
      const rec = a as Record<string, unknown>
      const audienceLabels = (rec.audienceLabels as Array<{ fullPath: string; tier: number; name: string }> | undefined) || []
      const audienceCountryCodes = audienceLabels
        .filter((l) => l.fullPath.includes('Country Extension') && l.tier === 6)
        .map((l) => COUNTRIES.find((c) => c.name === l.name)?.code)
        .filter(Boolean) as Array<string>

      if (!criteria.countryCodes.some((code) => audienceCountryCodes.includes(code))) return false
    }

    return true
  })
}
